export const buildParentIdMapping = (marketsParentageData) => {
    const mapping = {};
    marketsParentageData.forEach(({parentId, childId}) => {
        if (!mapping[childId]) {
            mapping[childId] = [];
        }
        mapping[childId].push(parentId);
    });
    return mapping;
};

export const buildChildIdMapping = (marketsParentageData) => {
    const mapping = {};
    marketsParentageData.forEach(({parentId, childId}) => {
        if (!mapping[parentId]) {
            mapping[parentId] = [];
        }
        mapping[parentId].push(childId);
    });
    return mapping;
};

export function buildMergedParentageMappings(parentIdMapping, childIdMapping, marketId, updateParentMarketIds, updateChildMarketIds) {
    const oldParents = parentIdMapping[marketId] || [];
    const oldChildren = childIdMapping[marketId] || [];

    const removedParents = oldParents.filter(parentId => !updateParentMarketIds.includes(parentId));
    const removedChildren = oldChildren.filter(childId => !updateChildMarketIds.includes(childId));

    const mergedParentIdMapping = {...parentIdMapping, [marketId]: updateParentMarketIds};
    removedChildren.forEach(childId => {
        mergedParentIdMapping[childId] = (mergedParentIdMapping[childId] || []).filter(id => id !== marketId);
    });

    const mergedChildIdMapping = {...childIdMapping, [marketId]: updateChildMarketIds};
    removedParents.forEach(parentId => {
        mergedChildIdMapping[parentId] = (mergedChildIdMapping[parentId] || []).filter(id => id !== marketId);
    });

    return {mergedParentIdMapping, mergedChildIdMapping};
}

export function checkParents(id, parentIds, childIds) {
    if (!parentIds[id]) {
        return [];
    }

    const invalidParentIds = [];
    for (const parentId of parentIds[id]) {
        if (childIds.includes(parentId)) {
            invalidParentIds.push(parentId);
            return [parentId];
        }

        if (checkParents(parentId, parentIds, childIds.concat(parentId)).length > 0) {
            invalidParentIds.push(parentId);
        }
    }

    return invalidParentIds.length > 0 ? invalidParentIds : [];
}

export function checkChildren(id, childIds, parentIds) {
    if (!childIds[id]) {
        return [];
    }

    const invalidChildIds = [];
    for (const childId of childIds[id]) {
        if (parentIds.includes(childId)) {
            invalidChildIds.push(childId);
            return [childId];
        }
        if (checkChildren(childId, childIds, parentIds.concat(childId)).length > 0) {
            invalidChildIds.push(childId);
        }
    }

    return invalidChildIds.length > 0 ? invalidChildIds : [];
}

export function checkParentDuplicates(id, parentIds) {
    const directParents = parentIds[id] || [];
    const directParentSet = new Set(directParents);
    const invalidParents = new Set();

    function collectAncestors(currentId, visited = new Set()) {
        if (visited.has(currentId)) {
            return [];
        }

        visited.add(currentId);

        const parents = parentIds[currentId] || [];
        const ancestors = [];

        for (const parentId of parents) {
            ancestors.push(parentId);
            ancestors.push(...collectAncestors(parentId, visited));
        }

        return ancestors;
    }

    for (const parentId of directParents) {
        const ancestors = collectAncestors(parentId);

        for (const ancestorId of ancestors) {
            if (directParentSet.has(ancestorId)) {
                invalidParents.add(ancestorId);
            }
        }
    }

    const allIds = Object.keys(parentIds);
    for (const nodeId of allIds) {
        if (nodeId === id) continue;

        const ancestorsOfNode = collectAncestors(nodeId);
        if (ancestorsOfNode.includes(Number(id))) {
            const parentSetOfNode = new Set(parentIds[nodeId] || []);

            for (const p of directParents) {
                const ancestorsOfP = [p, ...collectAncestors(p)];
                for (const a of ancestorsOfP) {
                    if (parentSetOfNode.has(a)) {
                        invalidParents.add(p);
                    }
                }
            }
        }
    }

    return Array.from(invalidParents);
}

export function checkChildrenDuplicates(id, childIds, parentIds) {
    const allChildIds = [];

    function collectAllChildren(parentId, childIds, visited) {
        const children = [];

        if (childIds[parentId]) {
            for (const childId of childIds[parentId]) {
                children.push(childId);

                if (childIds[childId] && !visited.has(childId)) {
                    visited.add(childId);
                    const nestedChildren = collectAllChildren(childId, childIds, visited);
                    children.push(...nestedChildren);
                }
            }
        }

        return children;
    }

    for (const parentId of parentIds) {
        if (childIds[parentId]) {
            for (const childId of childIds[parentId]) {
                allChildIds.push(childId);

                if (childIds[childId]) {
                    const nestedChildren = collectAllChildren(childId, childIds, new Set([childId]));
                    allChildIds.push(...nestedChildren);
                }
            }
        }
    }

    const seen = new Set();
    for (const childId of allChildIds) {
        if (seen.has(childId)) {
            return [childId];
        }
        seen.add(childId);
    }
    return [];
}
